import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wifi, 
  Settings, 
  RefreshCw, 
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Signal,
  Battery,
  Activity,
  TrendingUp,
  MapPin,
  Clock,
  BarChart3,
  Shield,
  Cpu,
  Zap
} from 'lucide-react';

const IoTDevices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [newDevice, setNewDevice] = useState({
    name: '',
    type: 'RFID Scanner',
    location: '',
    deviceId: '',
    description: ''
  });

  const [devices, setDevices] = useState([]);
  const [deviceStats, setDeviceStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    warning: 0
  });
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const deviceTypes = [
    'RFID Scanner',
    'Security Sensor',
    'Temperature Monitor',
    'Humidity Sensor',
    'Motion Detector',
    'Door Sensor',
    'Camera',
    'Access Control',
    'Environmental Monitor',
    'Network Device'
  ];

  // Load devices and stats on component mount
  useEffect(() => {
    loadDevices();
    loadDeviceStats();
  }, []);

  const loadDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/iot-devices/', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const loadDeviceStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/iot-devices/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDeviceStats({
          total: data.total_devices || 0,
          online: data.active_devices || 0,
          offline: data.offline_devices || 0,
          warning: data.warning_devices || 0
        });
      }
    } catch (error) {
      console.error('Failed to load device stats:', error);
    }
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/iot-devices/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newDevice.name,
          device_type: newDevice.type,
          location: newDevice.location,
          device_id: newDevice.deviceId,
          metadata: { description: newDevice.description }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Device added successfully');
        setMessageType('success');
        setShowAddModal(false);
        setNewDevice({
          name: '',
          type: 'RFID Scanner',
          location: '',
          deviceId: '',
          description: ''
        });
        
        // Reload devices and stats
        loadDevices();
        loadDeviceStats();
      } else {
        setMessage(data.error || 'Failed to add device');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const iotDevices = [
    { 
      id: 1, 
      name: 'RFID Scanner A1', 
      type: 'RFID Scanner', 
      location: 'Main Vault A', 
      status: 'online', 
      lastSeen: '2 minutes ago',
      signal: 95,
      battery: 87,
      temperature: '72°F',
      humidity: '45%',
      firmware: 'v2.1.4',
      uptime: '99.8%',
      dataTransmitted: '2.4 GB'
    },
    { 
      id: 2, 
      name: 'IoT Gateway B2', 
      type: 'Gateway', 
      location: 'Server Room', 
      status: 'online', 
      lastSeen: '1 minute ago',
      signal: 98,
      battery: 'N/A',
      temperature: '68°F',
      humidity: '35%',
      firmware: 'v1.9.2',
      uptime: '99.9%',
      dataTransmitted: '15.7 GB'
    },
    { 
      id: 3, 
      name: 'Environmental Sensor C3', 
      type: 'Sensor', 
      location: 'Range Storage B', 
      status: 'warning', 
      lastSeen: '5 minutes ago',
      signal: 72,
      battery: 23,
      temperature: '71°F',
      humidity: '43%',
      firmware: 'v1.7.1',
      uptime: '98.5%',
      dataTransmitted: '0.8 GB'
    },
    { 
      id: 4, 
      name: 'Security Camera D4', 
      type: 'Camera', 
      location: 'Main Entrance', 
      status: 'online', 
      lastSeen: '30 seconds ago',
      signal: 89,
      battery: 94,
      temperature: '73°F',
      humidity: '47%',
      firmware: 'v2.3.0',
      uptime: '99.7%',
      dataTransmitted: '8.2 GB'
    },
    { 
      id: 5, 
      name: 'Motion Detector E5', 
      type: 'Sensor', 
      location: 'Perimeter Fence', 
      status: 'offline', 
      lastSeen: '2 hours ago',
      signal: 0,
      battery: 0,
      temperature: 'N/A',
      humidity: 'N/A',
      firmware: 'v1.6.8',
      uptime: '0%',
      dataTransmitted: '0 GB'
    },
    { 
      id: 6, 
      name: 'Access Control F6', 
      type: 'Controller', 
      location: 'Main Vault B', 
      status: 'online', 
      lastSeen: '45 seconds ago',
      signal: 96,
      battery: 'N/A',
      temperature: '70°F',
      humidity: '41%',
      firmware: 'v2.0.1',
      uptime: '99.9%',
      dataTransmitted: '3.1 GB'
    }
  ];



  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'offline':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Wifi className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'offline':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'offline':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSignalColor = (signal) => {
    if (signal >= 80) return 'text-green-400';
    if (signal >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getBatteryColor = (battery) => {
    if (battery === 'N/A') return 'text-gray-400';
    if (battery >= 50) return 'text-green-400';
    if (battery >= 20) return 'text-yellow-400';
    return 'text-red-400';
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.device_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || device.status === filterStatus;
    const matchesType = filterType === 'all' || device.device_type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">IoT Devices</h1>
          <p className="text-gray-400 mt-1">Monitor and manage connected IoT devices for inventory tracking</p>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => { loadDevices(); loadDeviceStats(); }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Device
          </button>
        </div>
      </div>

      {/* Device Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Devices</p>
              <p className="text-2xl font-bold text-white">{deviceStats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Wifi className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-blue-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              <span>All monitored</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Online</p>
              <p className="text-2xl font-bold text-white">{deviceStats.online}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>Operational</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Healthy</p>
              <p className="text-2xl font-bold text-white">{deviceStats.healthy}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <Shield className="w-4 h-4 mr-1" />
              <span>Good condition</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Low Battery</p>
              <p className="text-2xl font-bold text-white">{deviceStats.lowBattery}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-yellow-400">
              <AlertTriangle className="w-4 h-4 mr-1" />
              <span>Needs attention</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name, type, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="online">Online</option>
            <option value="warning">Warning</option>
            <option value="offline">Offline</option>
          </select>
          
          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="RFID Scanner">RFID Scanner</option>
            <option value="Gateway">Gateway</option>
            <option value="Sensor">Sensor</option>
            <option value="Camera">Camera</option>
            <option value="Controller">Controller</option>
          </select>
        </div>
      </motion.div>

      {/* Device List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Connected Devices</h2>
        
        <div className="space-y-3">
          {filteredDevices.map((device) => (
            <motion.div
              key={device.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Wifi className="w-6 h-6 text-blue-400" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="font-medium text-white">{device.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(device.status)}`}>
                        {device.status}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium border bg-blue-500/20 text-blue-400 border-blue-500/30">
                        {device.device_type}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-400">
                      <div>
                        <span className="text-gray-500">Location:</span> {device.location}
                      </div>
                      <div>
                        <span className="text-gray-500">Firmware:</span> {device.metadata?.firmware_version || 'N/A'}
                      </div>
                      <div>
                        <span className="text-gray-500">Uptime:</span> {device.metadata?.uptime || 'N/A'}
                      </div>
                      <div>
                        <span className="text-gray-500">Last Seen:</span> {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm mt-2">
                      <div className="flex items-center space-x-2">
                        <Signal className={`w-4 h-4 ${getSignalColor(device.signal_strength)}`} />
                        <span className={getSignalColor(device.signal_strength)}>{device.signal_strength}%</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Battery className={`w-4 h-4 ${getBatteryColor(device.battery_level)}`} />
                        <span className={getBatteryColor(device.battery_level)}>{device.battery_level}%</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-400">{device.metadata?.temperature || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Activity className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-400">{device.metadata?.data_transmitted || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors" title="View Details">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-green-400 hover:bg-green-500/20 rounded transition-colors" title="Configure">
                    <Settings className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-purple-400 hover:bg-purple-500/20 rounded transition-colors" title="Restart">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors" title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Device Health</div>
                <div className="text-sm text-blue-200">Run diagnostics</div>
              </div>
            </div>
          </button>
          
          <button className="p-4 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Zap className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Firmware Update</div>
                <div className="text-sm text-green-200">Update all devices</div>
              </div>
            </div>
          </button>
          
          <button className="p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Cpu className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Network Config</div>
                <div className="text-sm text-purple-200">Configure settings</div>
              </div>
            </div>
          </button>
        </div>
      </motion.div>

      {/* Add Device Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Add New Device</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddDevice} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Device Name
                </label>
                <input
                  type="text"
                  value={newDevice.name}
                  onChange={(e) => setNewDevice({...newDevice, name: e.target.value})}
                  className="input-field w-full"
                  placeholder="Enter device name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Device Type
                </label>
                <select
                  value={newDevice.type}
                  onChange={(e) => setNewDevice({...newDevice, type: e.target.value})}
                  className="input-field w-full"
                  required
                >
                  {deviceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={newDevice.location}
                  onChange={(e) => setNewDevice({...newDevice, location: e.target.value})}
                  className="input-field w-full"
                  placeholder="Enter device location"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Device ID
                </label>
                <input
                  type="text"
                  value={newDevice.deviceId}
                  onChange={(e) => setNewDevice({...newDevice, deviceId: e.target.value})}
                  className="input-field w-full"
                  placeholder="Enter unique device ID"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={newDevice.description}
                  onChange={(e) => setNewDevice({...newDevice, description: e.target.value})}
                  className="input-field w-full h-20 resize-none"
                  placeholder="Enter device description"
                />
              </div>

              {message && (
                <div className={`p-3 rounded-lg ${
                  messageType === 'success' 
                    ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}>
                  {message}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 btn-primary"
                >
                  {isLoading ? 'Adding...' : 'Add Device'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IoTDevices;
